import { GeoPosition } from 'geo-position.ts/lib';
import { fromEventPattern, Observable, of, throwError } from 'rxjs';
import { distinctUntilChanged, map, shareReplay, switchMap } from 'rxjs/operators';
import { environment } from '../../../../environments/environment';
import { ICoordinatesLike } from '../../../models/coordinate-like.model.i';
import { DEFAULT_NAVIGATION_INFO, INavigationInfo } from '../../../models/navigation-info.model.i';
import { BrowserUtils } from './browser.utils';

interface WebkitDeviceOrientationEvent extends DeviceOrientationEvent {
    webkitCompassHeading: number;
}

type DeviceOrientationEventLike = WebkitDeviceOrientationEvent;

declare var screen: Screen & {
    mozOrientation?: OrientationType;
    msOrientation?: OrientationType;
    orientation: ScreenOrientation | OrientationType;
};

export enum SimpleOrientationType {
    PORTRAIT = 'portrait',
    LANDSCAPE = 'landscape'
}

export enum OrientationTypeMode {
    PRIMARY = 'primary',
    SECONDARY = 'secondary'
}


export namespace NavigationUtils {
    let position$: Observable<ICoordinatesLike> = null;

    export enum DeviceOrientationEventName {
        DEVICE_ORIENTATION_ABSOLUTE = 'deviceorientationabsolute',
        DEVICE_ORIENTATION = 'deviceorientation'
    }

    export function getDefaultScreenOrientationType(): SimpleOrientationType {
        if (screen.width > screen.height) {
            return SimpleOrientationType.LANDSCAPE;
        }

        return SimpleOrientationType.PORTRAIT;
    }

    export function getDeviceOrientation(): Observable<DeviceOrientationEventLike> {

        if (BrowserUtils.isChrome) {
            return fromEventPattern<DeviceOrientationEventLike>(
                handler => window.addEventListener(DeviceOrientationEventName.DEVICE_ORIENTATION_ABSOLUTE, handler),
                handler => window.removeEventListener(DeviceOrientationEventName.DEVICE_ORIENTATION_ABSOLUTE, handler)
            );
        }

        return fromEventPattern<DeviceOrientationEventLike>(
            handler => window.addEventListener(DeviceOrientationEventName.DEVICE_ORIENTATION, handler),
            handler => window.removeEventListener(DeviceOrientationEventName.DEVICE_ORIENTATION, handler)
        );
    }

    export async function needsDeviceOrientationPermission(): Promise<boolean> {
        if (!DeviceOrientationEvent) {
            return false;
        }

        try {
            // feature detect
            if (typeof DeviceOrientationEvent['requestPermission'] === 'function') {
                const result = await DeviceOrientationEvent['requestPermission']();

                if (result !== 'granted') {
                    throw new Error('Device orientation permission denied');
                }
            }

            return false;
        } catch (e) {
            console.error(e);
            return true;
        }
    }

    export async function requestDeviceOrientationPermission() {
        if (!DeviceOrientationEvent) {
            return;
        }

        // feature detect
        if (typeof DeviceOrientationEvent['requestPermission'] === 'function') {
            const result = await DeviceOrientationEvent['requestPermission']();

            if (result !== 'granted') {
                throw new Error('Device orientation permission denied');
            }
        } else {
            // handle regular non iOS 13+ devices
        }
    }

    function isScreenOrientation(value: any): value is ScreenOrientation {
        return value && value.type;
    }

    /**
     *  'portrait-primary':     for (screen width < screen height, e.g. phone, phablet, small tablet)
     *                              device is in 'normal' orientation
     *                          for (screen width > screen height, e.g. large tablet, laptop)
     *                              device has been turned 90deg clockwise from normal
     *  'portrait-secondary':   for (screen width < screen height)
     *                              device has been turned 180deg from normal
     *                          for (screen width > screen height)
     *                              device has been turned 90deg anti-clockwise (or 270deg clockwise) from normal
     *  'landscape-primary':    for (screen width < screen height)
     *                              device has been turned 90deg clockwise from normal
     *                          for (screen width > screen height)
     *                              device is in 'normal' orientation
     *  'landscape-secondary':  for (screen width < screen height)
     *                              device has been turned 90deg anti-clockwise (or 270deg clockwise) from normal
     *                          for (screen width > screen height)
     *                              device has been turned 180deg from normal
     */
    function getScreenOrientationType(): OrientationType {
        let result: OrientationType;

        if (isScreenOrientation(screen.orientation)) {
            result = screen.orientation.type;
        } else {
            result = screen.orientation || screen.mozOrientation || screen.msOrientation || 'portrait-primary';
        }

        return result;
    }

    function getScreenOrientationAdjustment(defaultOrientation: SimpleOrientationType = SimpleOrientationType.PORTRAIT): number {

        const screenOrientation = getScreenOrientationType();

        let adjustment = 0;

        if (defaultOrientation === SimpleOrientationType.LANDSCAPE) {
            adjustment -= 90;
        }

        if (screenOrientation != null) {
            const [simpleOrientationType, mode] = screenOrientation.split('-');

            if (defaultOrientation !== simpleOrientationType) {
                if (defaultOrientation === SimpleOrientationType.LANDSCAPE) {
                    adjustment -= 270;
                } else {
                    adjustment -= 90;
                }
            }

            if (mode === OrientationTypeMode.SECONDARY) {
                adjustment -= 180;
            }
        }

        return adjustment;
    }

    export function getHeading(defaultOrientation?: SimpleOrientationType): Observable<number> {
        const orientation$ = getDeviceOrientation();

        return orientation$.pipe(
            map(orientationEvent => {
                if (orientationEvent.webkitCompassHeading != null) {
                    return 360 - orientationEvent.webkitCompassHeading; // iOS non-standard
                }

                if (!orientationEvent.absolute) {
                    return 0;
                }

                return orientationEvent.alpha;
            }),
            map(heading => heading + getScreenOrientationAdjustment(defaultOrientation)),
            map(heading => heading < 0 ? heading + 360 : heading),
            distinctUntilChanged()
        );
    }

    export function getCurrentPosition(): Observable<ICoordinatesLike> {

        if (!position$) {
            const {geolocation} = navigator;

            if (!geolocation) {
                console.error('NavigationUtils getCurrentPosition: No geolocation API found');
                return throwError('No geolocation API found');
            }

            position$ = new Observable<ICoordinatesLike>(subscriber => {
                const watchId = geolocation.watchPosition(
                    position => {
                        subscriber.next(position ? position.coords : void 0);
                    }, error => {
                        subscriber.error(error);
                    }, {enableHighAccuracy: true});

                return () => {
                    geolocation.clearWatch(watchId);
                    subscriber.complete();
                };

            }).pipe(
                map(coordinate => {
                    if (!environment.production && coordinate.latitude > 48.9 && coordinate.longitude < 12.2) {
                        return {
                            latitude: coordinate.latitude - 0.594168,
                            longitude: coordinate.longitude + 1.114622
                        };
                    }

                    return coordinate;
                }),
                shareReplay()
            );
        }

        return position$;
    }

    export function getCurrentNavigationInfo(to: ICoordinatesLike,
                                             defaultOrientation?: SimpleOrientationType): Observable<INavigationInfo> {
        return getCurrentPosition()
            .pipe(
                switchMap(currentPosition => getNavigationInfo(currentPosition, to, defaultOrientation))
            );
    }

    export function getNavigationInfo(from: ICoordinatesLike,
                                      to: ICoordinatesLike,
                                      defaultOrientation?: SimpleOrientationType): Observable<INavigationInfo> {

        if (!from || !to) {
            return of(DEFAULT_NAVIGATION_INFO);
        }

        return getHeading(defaultOrientation)
            .pipe(
                map(heading => _calculateNavigationInfo(from, to, heading))
            );
    }

    function _calculateNavigationInfo(from: ICoordinatesLike, to: ICoordinatesLike, heading: number): INavigationInfo {

        const result = {
            direction: _calculateBearing(from.latitude, from.longitude, to.latitude, to.longitude) + heading,
            north: heading,
            distance: _calculateDistance(from, to)
        };

        return result;
    }

    function _calculateDistance(from: ICoordinatesLike, to: ICoordinatesLike): number {

        const position = new GeoPosition(from.latitude, from.longitude);
        const targetPosition = new GeoPosition(to.latitude, to.longitude);

        return position.Distance(targetPosition);
    }

    function _calculateBearing(lat1: number, lng1: number, lat2: number, lng2: number): number {

        lat1 = _toRad(lat1);
        lng1 = _toRad(lng1);
        lat2 = _toRad(lat2);
        lng2 = _toRad(lng2);

        const dLon = (lng2 - lng1);
        const y = Math.sin(dLon) * Math.cos(lat2);
        const x = Math.cos(lat1) * Math.sin(lat2) - Math.sin(lat1) * Math.cos(lat2) * Math.cos(dLon);
        const rad = Math.atan2(y, x);

        return _toDeg(rad); // 360 - ((bearing + 360) % 360);
    }

    function _toDeg(rad): number {
        return rad * 180 / Math.PI;
    }

    function _toRad(deg): number {
        return deg * Math.PI / 180;
    }
}
